import React, { useEffect, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Switch,
  Text,
  Textarea,
  Tooltip,
} from '@chakra-ui/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { LockSimple } from 'phosphor-react';
import { toast } from 'sonner';

import { IApiRecurso, useApiController } from '../../../hooks/useApiController';

interface Props {
  recurso: IApiRecurso | null;
  onClose: () => void;
}

// Ver config da rota + editar nome/descrição + suspender/reativar (campo `ativo`).
// As TABELAS ficam TRANCADAS (read-only) — mudar tabela = criar outra rota.
export function RotaDetalhesModal({ recurso, onClose }: Props) {
  const queryClient = useQueryClient();
  const { updateRecurso } = useApiController();

  const [nome, setNome] = useState('');
  const [descricao, setDescricao] = useState('');
  const [ativo, setAtivo] = useState(true);

  useEffect(() => {
    if (recurso) {
      setNome(recurso.nome);
      setDescricao(recurso.descricao ?? '');
      setAtivo(recurso.ativo);
    }
  }, [recurso]);

  const { mutateAsync, isPending } = useMutation({
    mutationFn: updateRecurso,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['api-controller-recursos'] });
      toast.success('Rota atualizada', { position: 'top-right' });
      onClose();
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.detail ?? 'Erro ao atualizar rota', {
        position: 'top-right',
      });
    },
  });

  const handleSalvar = async () => {
    if (!recurso) return;
    await mutateAsync({
      id: recurso.id,
      data: { nome: nome.trim(), descricao: descricao.trim() || null, ativo },
    });
  };

  const tabelas = recurso?.tabelas ?? [];

  return (
    <Modal isOpen={!!recurso} onClose={onClose} size="2xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxH="90vh">
        <ModalHeader>Configurações da rota</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Box display="flex" flexDirection="column" gap={4}>
            <FormControl>
              <FormLabel>Nome</FormLabel>
              <Input value={nome} onChange={e => setNome(e.target.value)} />
            </FormControl>

            <FormControl>
              <FormLabel>Descrição</FormLabel>
              <Textarea
                value={descricao}
                onChange={e => setDescricao(e.target.value)}
                rows={2}
                placeholder="Opcional"
              />
            </FormControl>

            <FormControl display="flex" alignItems="center" gap={3}>
              <Switch isChecked={ativo} onChange={e => setAtivo(e.target.checked)} />
              <Box>
                <FormLabel mb={0}>{ativo ? 'Ativa' : 'Suspensa'}</FormLabel>
                <Text fontSize="xs" color="gray.500">
                  Rota suspensa não serve dados (não apaga).
                </Text>
              </Box>
            </FormControl>

            {/* Tabelas: TRANCADAS (read-only) */}
            <FormControl>
              <Flex align="center" gap={2} mb={1}>
                <FormLabel mb={0}>Tabelas de acesso</FormLabel>
                <Tooltip label="As tabelas de uma rota não podem ser alteradas. Para outro conjunto, crie uma nova rota.">
                  <Flex align="center" gap={1} color="gray.500">
                    <LockSimple size={14} weight="fill" />
                    <Text fontSize="xs">trancado</Text>
                  </Flex>
                </Tooltip>
              </Flex>
              <Box borderWidth="1px" borderRadius="md" p={2} bg="gray.50">
                {tabelas.length > 0 ? (
                  tabelas.map(t => (
                    <Flex key={t.tabela_fonte} justify="space-between" py={1}>
                      <Text fontSize="sm" fontWeight="500">
                        {t.tabela_fonte}
                        <Text as="span" fontSize="xs" color="gray.400" ml={2}>
                          /external/v1/{t.slug}/
                        </Text>
                      </Text>
                      <Badge colorScheme="gray">{t.colunas.length} colunas</Badge>
                    </Flex>
                  ))
                ) : (
                  <Text fontSize="sm" color="gray.400">
                    {recurso?.tabela_fonte}
                  </Text>
                )}
              </Box>
            </FormControl>
          </Box>
        </ModalBody>
        <ModalFooter gap={2}>
          <Button variant="outline" onClick={onClose} isDisabled={isPending}>
            Cancelar
          </Button>
          <Button onClick={handleSalvar} isLoading={isPending} isDisabled={nome.trim().length < 2}>
            Salvar
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
